import { createFeatureSelector, createSelector } from '@ngrx/store';

import { Contract } from '../../interfaces/contracts';
import {
    BackupPaymentStatus,
    LeaseBackupStatus,
    PaymentMethodState,
    PilotVariant,
} from '../../interfaces/payment-method';
import {
    getContractByIdSelector,
    getContractListSelector,
    getContractSelectedIdSelector,
} from '../contracts/contracts.selectors';
import { PaymentMethodFeatureName } from './payment-method.reducer';

const isPilot = (variant: PilotVariant | undefined): boolean =>
    variant === 'PILOT_A' || variant === 'PILOT_B';

/**
 * The lease's backup status in the vocabulary UserPilot segments on.
 *
 * Two vocabularies meet here and neither should have to learn the other's: the server speaks
 * its enum, UserPilot its property values. This is the one place they are joined.
 *
 * A lease with no status is not translated at all. It means rose-portal-service could not
 * answer, and any of the three answers would be an invention — one that either sends a ROSE to
 * add a card she has, or tells her a working card was declined.
 */
const toBackupPaymentStatus = (
    status: LeaseBackupStatus | undefined
): BackupPaymentStatus | undefined => {
    switch (status) {
        case 'NONE': return 'none';
        case 'ACTIVE': return 'active';
        case 'FAILED': return 'failed';
        default: return undefined;
    }
};

/**
 * The status of one lease, read off the contract alone.
 *
 * The contract is the single source of truth for this, including in the middle of a session:
 * saving a card writes `ACTIVE` onto the lease itself (see the contracts reducer), so there is
 * no second, fresher place to consult and no chance of the two disagreeing. An earlier version
 * preferred the loaded-card map here, which could only report whether a card existed — enough
 * to answer "none or not", but blind to a card on file that had been declined.
 */
const leaseBackupStatus = (contract: Contract): BackupPaymentStatus | undefined =>
    toBackupPaymentStatus(contract.backupPaymentStatus);

export const PaymentMethodFeatureSelector =
    createFeatureSelector<PaymentMethodState>(PaymentMethodFeatureName);

/**
 * Whether a lease gets the new Payment methods experience. A cohort we do not have — an older
 * service, or one that could not resolve it — reads as false, so the legacy modal is what shows
 * until we positively know otherwise.
 */
export const isPilotLeaseSelector = (leaseKey: string) => createSelector(
    getContractByIdSelector(leaseKey),
    (contract) => isPilot(contract?.pilotVariant)
);

/**
 * The same question `backupPaymentStatus` answers, asked of one lease instead of the ROSE.
 *
 * This is what the in-app nudge fires on: she is looking at a lease, and it is this lease that
 * does or does not need attention. The facts ride on the contract already, so answering costs
 * nothing — no request, no waiting on a second round trip.
 *
 * An unresolved lease reads `not_applicable`, the same as a control lease. Both mean "say
 * nothing here", and there is no third thing to say: nudging on an absent answer would tell a
 * ROSE to fix a card the portal never managed to look at.
 */
export const getLeaseBackupStatusSelector = (leaseKey: string) => createSelector(
    getContractByIdSelector(leaseKey),
    (contract): BackupPaymentStatus => {
        if (!contract || !isPilot(contract.pilotVariant)) {
            return 'not_applicable';
        }
        return leaseBackupStatus(contract) ?? 'not_applicable';
    }
);

/** The card the open modal is about — the lease currently selected. */
export const getBackupPaymentMethodSelector = createSelector(
    PaymentMethodFeatureSelector,
    getContractSelectedIdSelector,
    (state: PaymentMethodState, leaseKey: string) => state.backupMethods[leaseKey] ?? null
);

/** True only once the load has finished and came back empty — drives the "add a card" state. */
export const hasNoBackupPaymentMethodSelector = createSelector(
    PaymentMethodFeatureSelector,
    getContractSelectedIdSelector,
    (state: PaymentMethodState, leaseKey: string) =>
        leaseKey in state.backupMethods && state.backupMethods[leaseKey] === null
);

export const getPaymentSessionSelector = createSelector(
    PaymentMethodFeatureSelector,
    (state: PaymentMethodState) => state.session
);

export const isPaymentMethodLoadingSelector = createSelector(
    PaymentMethodFeatureSelector,
    (state: PaymentMethodState) => state.loading
);

export const isPaymentMethodSavingSelector = createSelector(
    PaymentMethodFeatureSelector,
    (state: PaymentMethodState) => state.saving
);

export const isPaymentMethodSavedSelector = createSelector(
    PaymentMethodFeatureSelector,
    (state: PaymentMethodState) => state.saved
);

export const getPaymentMethodErrorSelector = createSelector(
    PaymentMethodFeatureSelector,
    (state: PaymentMethodState) => state.error
);

/**
 * The `backupPaymentStatus` user property UserPilot segments on (OPS-2288).
 *
 * A roll-up, because the property is per ROSE while its inputs are per lease: the pilot cohort
 * is stamped on the application, and so is the card. Every input rides on the lease list, so
 * this is derived from a call the portal already makes.
 *
 * Worst-first, and `none` is the worst. A lease with no method at all is unprotected, which is
 * a bigger ask than a lease whose method needs replacing, so a ROSE with one of each is asked
 * for the card she does not have rather than told about the one that failed — she can only be
 * shown one message, and that is the one that leaves her covered. `failed` outranks `active`
 * for the same reason: a lease in trouble is not cancelled out by a lease that is fine.
 *
 * A lease whose status is genuinely unknown — an older service, a lookup that failed behind the
 * list — counts for nothing at all: only a definite answer may raise a nudge. Everything
 * unknown therefore lands on `not_applicable`, which is also what a ROSE reads before her
 * leases arrive and what a control-group ROSE reads for good. The nudge fails closed — a slow
 * or degraded response delays the message rather than firing it at someone it was never meant
 * for — and `Userpilot.reload()` on the next navigation re-evaluates once the answer lands.
 */
export const getBackupPaymentStatusSelector = createSelector(
    getContractListSelector,
    (contracts): BackupPaymentStatus => {
        const resolved = contracts
            .filter(({ pilotVariant }) => isPilot(pilotVariant))
            .map(leaseBackupStatus)
            .filter((status): status is BackupPaymentStatus => status !== undefined);

        if (resolved.length === 0) {
            return 'not_applicable';
        }

        return resolved.find((status) => status === 'none')
            ?? resolved.find((status) => status === 'failed')
            ?? 'active';
    }
);
